import type { Spritesheet, Texture } from "pixi.js";
import { SpriteFontDefinition } from "@slot/presentation";
import type { Log, SpriteFontConfigJson } from "@slot/presentation";
import type { BootLaunchPayload, BrandedAssetResolver } from "@slot/game-shell";
import { MERGE_LADDER, type LadderSymbolId } from "./mergeLadder";
import { drawSecretBonusSymbol } from "./secretBonusSymbol";

/**
 * Everything the round's visuals need, resolved ONCE from the loaded bundles through the branded resolver (operator
 * bundle first for allowlisted paths). Scene builders and views take this bag; nothing else touches the registry.
 * The nine symbols are the required entries: `getAssetOrThrow` throws when one is missing, uncaught, and the boot
 * boundary shows the boot-failure popup. Missing optional art resolves to null and is logged, so the game still boots.
 */
export interface GameVisualAssets {
  /** The grid's symbols, by id (the art the cells and the merge show). */
  readonly symbols: ReadonlyMap<LadderSymbolId, Texture>;
  /** The ladder bar's small icons, by symbol id. */
  readonly ladderIcons: ReadonlyMap<LadderSymbolId, Texture>;
  readonly textures: {
    readonly gridFrame: Texture | null;
    readonly ladderBar: Texture | null;
    readonly cellTile: Texture | null;
    /** The candy card frames and buttons (the round panels' art). */
    readonly cardPink: Texture | null;
    readonly cardPurple: Texture | null;
    readonly cardGreen: Texture | null;
    readonly buttonGreen: Texture | null;
    readonly buttonOrange: Texture | null;
  };
  readonly fonts: {
    /** The cell multiplier badges' digits and "x". */
    readonly multiplier: SpriteFontDefinition | null;
  };
  /** The Secret Bonus (decision 51): its symbol (the gold disc, lettered at boot) and the hand that points at it. */
  readonly secretBonus: {
    readonly symbol: Texture | null;
    readonly hand: Texture | null;
  };
  /** The chef's Rive file's URL (served with the bundle, fetched by the chef's view, not loaded by Pixi). */
  readonly chef: string | null;
  /** The background's Rive file's URL (served with the bundle, fetched by the background's layer, not loaded by Pixi); its stills are the shell's (decision 69). */
  readonly background: string | null;
}

export function loadGameVisualAssets(input: { payload: BootLaunchPayload; log: Log; /** The Secret Bonus symbol's lettering, localized. */ secretBonusLabel: string }): GameVisualAssets {
  const { payload, log } = input;
  const branded: BrandedAssetResolver = payload.brandedAssets;
  const optional = (path: string): Texture | null => {
    const asset = branded.getAssetOrNull<Texture>(path);
    if (asset === null) log(`[GameVisualAssets] texture "${path}" not loaded`);
    return asset;
  };
  const ladderIcons = new Map<LadderSymbolId, Texture>();
  for (const id of MERGE_LADDER) {
    const icon = optional(`symbols/ladder/${id}`);
    if (icon) ladderIcons.set(id, icon);
  }
  const secretBonusDisc = optional("symbols/SB");
  return {
    symbols: new Map(MERGE_LADDER.map((id) => [id, branded.getAssetOrThrow<Texture>(`symbols/${id}`)])),
    ladderIcons,
    textures: {
      gridFrame: optional("backgrounds/GridFrame"),
      ladderBar: optional("backgrounds/LadderBar"),
      cellTile: optional("symbols/cell_tile"),
      cardPink: optional("panels/card_pink"),
      cardPurple: optional("panels/card_purple"),
      cardGreen: optional("panels/card_green"),
      buttonGreen: optional("panels/button_green"),
      buttonOrange: optional("panels/button_orange"),
    },
    fonts: { multiplier: loadSpriteFont(branded, "multiplierFont", log) },
    secretBonus: { symbol: secretBonusDisc ? drawSecretBonusSymbol(secretBonusDisc, input.secretBonusLabel) : null, hand: optional("secret-bonus/hand") },
    chef: payload.assets.resolveUrl(payload.sharedBundleName, "chef/candy-chef"),
    background: payload.assets.resolveUrl(payload.sharedBundleName, "backgrounds/candy-factory-bg"),
  };
}

function loadSpriteFont(branded: BrandedAssetResolver, name: string, log: Log): SpriteFontDefinition | null {
  const sheet = branded.getAssetOrNull<Spritesheet>(`fonts/atlases/${name}`);
  const config = branded.getAssetOrNull<SpriteFontConfigJson>(`fonts/configs/${name}`);
  if (!sheet || !config) {
    log(`[GameVisualAssets] sprite font "${name}" not loaded (sheet=${!!sheet} config=${!!config})`);
    return null;
  }
  return SpriteFontDefinition.fromConfig({ config, atlas: sheet.textures, log });
}
